import {
  ParamMetadataService,
  createEmptyMetadataIndex,
  paramPlatformOf,
  type ParamFetcher,
  type ParamMetadataCache,
  type ParamMetadataIndex,
  type ParamMetadataSourceStatus,
  type ParamPlatform,
  type ParamTranslations,
  type VehicleClass,
} from '@hgc/core';

/**
 * 参数元数据的取用。
 *
 * 三条规则写在最前面，因为它们是这个文件存在的理由：
 *
 * 1. **拿不到元数据不是错误状态。** 飞机在野外没有网，pdef 拉不下来，这时参数照常
 *    下载、照常读写，只是少了中文说明与范围提示。因此这里的 `index` 永远非 null
 *    （降级时是空索引），调用方一句判空都不用写。
 * 2. **固件版本未知就写 'unknown'。** 缓存键是 `机型@版本`，编一个版本号会让缓存
 *    命中错的定义文件 —— 那比没有缓存危险得多（用户会看到另一版固件的说明）。
 * 3. **平台认不出来时不去猜。** `tracker` / `other` 在上游没有参数定义目录，
 *    猜成 Copter 的后果是给用户看另一台机型的中文说明。
 */

/** 车辆类别 → 上游 pdef 目录。`other` / `unknown` 在上游没有对应目录，返回 null */
export function paramPlatformForClass(vehicleClass: VehicleClass): ParamPlatform | null {
  if (vehicleClass === 'other' || vehicleClass === 'unknown') return null;
  return paramPlatformOf(vehicleClass);
}

export interface ParamMetadataState {
  readonly status: ParamMetadataSourceStatus | null;
  readonly size: number;
  /** 拿不到的原因（离线、机型未知…）；正常时为 null */
  readonly reason: string | null;
  readonly loading: boolean;
  readonly url: string | null;
}

export interface ParamMetadataLoaderOptions {
  readonly platform: ParamPlatform | null;
  readonly firmwareVersion: string;
  readonly cache?: ParamMetadataCache | null;
  readonly fetcher?: ParamFetcher;
  readonly translations?: ParamTranslations | null;
  readonly now?: () => number;
}

export class ParamMetadataLoader {
  private readonly service: ParamMetadataService | null;
  private readonly unavailableReason: string | null;

  private currentIndex: ParamMetadataIndex;
  private stateValue: ParamMetadataState;
  private inFlight: Promise<ParamMetadataIndex> | null = null;

  constructor(options: ParamMetadataLoaderOptions) {
    this.unavailableReason =
      options.platform === null
        ? '机型未识别（或该机型在上游没有参数定义目录），无法获取参数说明与取值范围'
        : null;

    if (options.platform === null) {
      this.service = null;
      this.currentIndex = createEmptyMetadataIndex(this.unavailableReason);
      this.stateValue = {
        status: 'empty',
        size: 0,
        reason: this.unavailableReason,
        loading: false,
        url: null,
      };
      return;
    }

    this.service = new ParamMetadataService({
      platform: options.platform,
      firmwareVersion: options.firmwareVersion,
      ...(options.cache === undefined || options.cache === null ? {} : { cache: options.cache }),
      ...(options.fetcher === undefined ? {} : { fetcher: options.fetcher }),
      ...(options.translations === undefined || options.translations === null
        ? {}
        : { translations: options.translations }),
      ...(options.now === undefined ? {} : { now: options.now }),
    });
    this.currentIndex = createEmptyMetadataIndex('参数元数据尚未加载');
    this.stateValue = {
      status: null,
      size: 0,
      reason: null,
      loading: true,
      url: this.service.url,
    };
  }

  /** 当前的索引。**永远非 null**：拿不到时是空索引（见文件头规则 1） */
  get index(): ParamMetadataIndex {
    return this.currentIndex;
  }

  get state(): ParamMetadataState {
    return this.stateValue;
  }

  /** 加载（并发调用共用同一次请求）。`load()` 永不抛错；失败也会返回空索引 */
  load(force = false): Promise<ParamMetadataIndex> {
    const service = this.service;
    if (service === null) return Promise.resolve(this.currentIndex);
    if (this.inFlight !== null) return this.inFlight;

    this.stateValue = { ...this.stateValue, loading: true };
    const run = (force ? service.refresh() : service.load())
      .then((result) => {
        this.currentIndex = result.index;
        this.stateValue = {
          status: result.status,
          size: result.index.size,
          reason: result.error,
          loading: false,
          url: result.url,
        };
        return result.index;
      })
      .finally(() => {
        this.inFlight = null;
      });
    this.inFlight = run;
    return run;
  }

  dispose(): void {
    this.service?.dispose();
    this.inFlight = null;
  }
}
